import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import { authChangePassword, saveLoginResponse } from '../api/client';
import { getCurrentEmail } from '../lib/auth';

interface ChangePasswordData {
  currentPassword: string;
  newPassword:     string;
  confirmPassword: string;
  error:           string;
  loading:         boolean;
}

/**
 * data-name 으로 받을 수 있는 편집 가능 필드 — type guard 패턴.
 */
type EditableField = 'currentPassword' | 'newPassword' | 'confirmPassword';

function isEditableField(name: string): name is EditableField {
  return (
    name === 'currentPassword' ||
    name === 'newPassword' ||
    name === 'confirmPassword'
  );
}

/**
 * 비밀번호 변경 페이지 (P6 — Admin Login).
 *
 * Sola 패턴 (RfqCreateView 와 동일):
 *   - events 객체 매핑 (data-action)
 *   - 부분 갱신 (setError / setLoading / markErrorField) — rerenderAll 회피
 *   - 에러 div 항상 그려두고 .hidden class 토글
 *   - 정책 위반 시 해당 필드에 .has-error class 부여 (빨간 테두리)
 *
 * 정책 (서버 ChangePasswordSchema 와 일치):
 *   - 최소 8자, 최대 72자
 *   - 영문 + 숫자 포함
 *   - 'hcltech1234' (default) 와 다름
 *   - 새 비번 두 입력란 일치
 */
export class ChangePasswordView extends SolaView<ChangePasswordData> {
  override events = {
    click: {
      '@submit': 'handleSubmit',
    },
    input: {
      '@field': 'handleFieldChange',
    },
    change: {
      '@field': 'handleFieldChange',
    },
  };

  constructor() {
    super();
    this.data = {
      currentPassword: '',
      newPassword:     '',
      confirmPassword: '',
      error:           '',
      loading:         false,
    };
  }

  override getTitle(): string {
    return '비밀번호 변경 — HCLTech 관리자';
  }

  // ─── 이벤트 핸들러 ───────────────────────────────────────────────
  handleFieldChange(_e: Event, target: HTMLElement): void {
    const name = target.getAttribute('data-name');
    if (!name || !isEditableField(name)) return;
    this.data[name] = (target as HTMLInputElement).value;
    // 입력 시 에러 클리어 + 모든 필드의 has-error 제거
    if (this.data.error) {
      this.setError('');
      this.clearFieldErrors();
    }
  }

  /**
   * 클라이언트 사이드 정책 검증.
   *
   * 반환: { message, field } — 에러 메시지 + 관련 필드 이름.
   *       null 이면 통과.
   */
  private validate(): { message: string; field: EditableField | null } | null {
    const { newPassword, confirmPassword } = this.data;
    if (newPassword.length < 8)
      return { message: '비밀번호는 8자 이상이어야 합니다', field: 'newPassword' };
    if (newPassword.length > 72)
      return { message: '비밀번호는 72자 이하여야 합니다', field: 'newPassword' };
    if (!/[A-Za-z]/.test(newPassword))
      return { message: '비밀번호에 영문이 포함되어야 합니다', field: 'newPassword' };
    if (!/[0-9]/.test(newPassword))
      return { message: '비밀번호에 숫자가 포함되어야 합니다', field: 'newPassword' };
    if (newPassword === 'hcltech1234')
      return { message: '기본 비밀번호와 다른 값을 사용하세요', field: 'newPassword' };
    if (newPassword !== confirmPassword)
      return { message: '새 비밀번호 확인이 일치하지 않습니다', field: 'confirmPassword' };
    return null;
  }

  async handleSubmit(_e?: Event): Promise<void> {
    if (this.data.loading) return;

    const { currentPassword, newPassword } = this.data;
    if (!currentPassword) {
      this.setError('현재 비밀번호를 입력하세요');
      this.markErrorField('currentPassword');
      return;
    }

    const validationError = this.validate();
    if (validationError) {
      this.setError(validationError.message);
      if (validationError.field) {
        this.markErrorField(validationError.field);
      }
      return;
    }

    this.setError('');
    this.clearFieldErrors();
    this.setLoading(true);

    try {
      const res = await authChangePassword(currentPassword, newPassword);
      const email = getCurrentEmail() ?? '';
      saveLoginResponse(res, email);

      history.pushState(null, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch (err) {
      this.setLoading(false);
      const msg = err instanceof Error ? err.message : '비밀번호 변경 실패';
      this.setError(msg);
      // 403 = 현재 비번 틀림 (서버 에러 메시지에서 추정)
      if (msg.includes('현재 비밀번호')) {
        this.markErrorField('currentPassword');
      }
    }
  }

  // ─── 부분 갱신 헬퍼 ──────────────────────────────────────────────
  private setError(msg: string): void {
    this.data.error = msg;
    const el = this._rootElement?.querySelector<HTMLElement>('.auth-error');
    if (!el) return;
    if (msg) {
      el.textContent = msg;
      el.classList.remove('hidden');
    } else {
      el.textContent = '';
      el.classList.add('hidden');
    }
  }

  /** 특정 필드의 wrapper 에 .has-error 추가 (빨간 테두리). */
  private markErrorField(field: EditableField): void {
    this.clearFieldErrors();
    const input = this._rootElement?.querySelector<HTMLInputElement>(
      `input[data-name="${field}"]`,
    );
    input?.closest('.auth-field')?.classList.add('has-error');
  }

  private clearFieldErrors(): void {
    const fields = this._rootElement?.querySelectorAll('.auth-field.has-error');
    fields?.forEach((f) => f.classList.remove('has-error'));
  }

  private setLoading(on: boolean): void {
    this.data.loading = on;
    const root = this._rootElement;
    if (!root) return;
    const btn = root.querySelector<HTMLButtonElement>('[data-action="submit"]');
    if (btn) {
      btn.disabled = on;
      btn.textContent = on ? '변경 중...' : '비밀번호 변경';
    }
    const inputs = root.querySelectorAll<HTMLInputElement>('input[data-action="field"]');
    inputs.forEach((i) => { i.disabled = on; });
  }

  private escAttr(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  }

  // ─── 렌더 ────────────────────────────────────────────────────────
  render(): HTMLElement {
    const { currentPassword, newPassword, confirmPassword, error, loading } = this.data;
    const email     = getCurrentEmail();
    const disabled  = loading ? 'disabled' : '';
    const errHidden = error ? '' : 'hidden';

    return htmlSolaWithInstance(this)`
      <div class="page page-auth">
        <div class="auth-card auth-card-wide">
          <header class="auth-card-header">
            <h1>비밀번호 변경</h1>
            <p class="auth-subtitle">
              ${email ? `${this.escAttr(email)} — 새 비밀번호를 설정하세요` : '새 비밀번호를 설정하세요'}
            </p>
          </header>

          <div class="auth-form">
            <div class="auth-field">
              <label for="cp-current">현재 비밀번호</label>
              <input
                id="cp-current"
                type="password"
                autocomplete="current-password"
                data-action="field"
                data-name="currentPassword"
                value="${this.escAttr(currentPassword)}"
                ${disabled}
              />
            </div>

            <div class="auth-field">
              <label for="cp-new">새 비밀번호</label>
              <input
                id="cp-new"
                type="password"
                autocomplete="new-password"
                data-action="field"
                data-name="newPassword"
                value="${this.escAttr(newPassword)}"
                ${disabled}
              />
              <small class="auth-hint">8자 이상, 영문 + 숫자 포함, 기본 비밀번호 금지</small>
            </div>

            <div class="auth-field">
              <label for="cp-confirm">새 비밀번호 확인</label>
              <input
                id="cp-confirm"
                type="password"
                autocomplete="new-password"
                data-action="field"
                data-name="confirmPassword"
                value="${this.escAttr(confirmPassword)}"
                ${disabled}
              />
            </div>

            <div class="auth-error ${errHidden}">${this.escAttr(error)}</div>

            <button
              type="button"
              class="auth-submit"
              data-action="submit"
              ${disabled}
            >
              ${loading ? '변경 중...' : '비밀번호 변경'}
            </button>
          </div>
        </div>
      </div>
    `;
  }
}
